import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { supabase } from '../../lib/supabase';
import { Property, searchSaleProperties, searchRentProperties, searchSoldProperties } from '../../lib/realEstateService';
import { Filters } from './types';
import { rentPropertyTypes, buyPropertyTypes } from './utils';
import { propertyCache, generateCacheKey } from '../../lib/utils/centralized-cache';
import { convertPropertyTypesForApi } from './utils';

export function useHomeState() {
	// Add state for HOA filter at the very top of the component
	const [maxHoaFees, setMaxHoaFees] = useState<string>('');
	const [noHoa, setNoHoa] = useState(false);
	const [searchQuery, setSearchQuery] = useState<string>('');
	const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
	const [isSearching, setIsSearching] = useState(false);
	const [listings, setListings] = useState<Property[]>([]);
	const latestListingsLenRef = useRef<number>(0);
	useEffect(() => {
		latestListingsLenRef.current = listings.length;
	}, [listings]);
	const [location, setLocation] = useLocation();
	const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState<Filters>({
		priceRange: [null, null],
		beds: null,
		baths: null,
		propertyType: null,
		hidePendingContingent: false,
		newConstructionOnly: false,
		hideHomesNotYetBuilt: false,
		daysOnMarket: null,
	});
	const [sort, setSort] = useState<'relevance' | 'price_asc' | 'price_desc'>('relevance');
	const [activeFilterTab, setActiveFilterTab] = useState<'buy' | 'rent' | 'sold'>('buy');
	
	// Info modal state
	const [infoModal, setInfoModal] = useState<null | 'pending' | 'hoa'>(null);
	
	// Property type modal state
	const [showPropertyTypeModal, setShowPropertyTypeModal] = useState(false);
	const [showAmenitiesModal, setShowAmenitiesModal] = useState(false);
	const [showSquareFeetModal, setShowSquareFeetModal] = useState(false);
	const [showLotSizeModal, setShowLotSizeModal] = useState(false);
	
	// Home age dropdown state (must be declared before any use)
	const [showHomeAgeDropdown, setShowHomeAgeDropdown] = useState(false);
	const [minHomeAge, setMinHomeAge] = useState<string>('');
	const [maxHomeAge, setMaxHomeAge] = useState<string>('');
	const [showDaysOnMarketModal, setShowDaysOnMarketModal] = useState(false);
	
	// Instead of a single propertyTypePillTouched, use a separate state for each tab
	const [propertyTypePillTouchedRent, setPropertyTypePillTouchedRent] = useState(false);
	const [propertyTypePillTouchedBuySold, setPropertyTypePillTouchedBuySold] = useState(false);
	
	const [resetBouncing, setResetBouncing] = useState(false);
	const [daysOnMarketResetBouncing, setDaysOnMarketResetBouncing] = useState(false);
	const [amenitiesResetBouncing, setAmenitiesResetBouncing] = useState(false);
	
	// Add state for warning message
	const [propertyTypeWarning, setPropertyTypeWarning] = useState('');
	
	
	// Add state for the Search Area modal and selected miles
	const [buySearchAreaMiles, setBuySearchAreaMiles] = useState(0);
	const [rentSearchAreaMiles, setRentSearchAreaMiles] = useState(0);
	const [soldSearchAreaMiles, setSoldSearchAreaMiles] = useState(0);
	const [showSearchAreaDropdown, setShowSearchAreaDropdown] = useState(false);
	
	// Instead of a single pendingSearchAreaMiles, use a separate state for each tab
	const [pendingBuySearchAreaMiles, setPendingBuySearchAreaMiles] = useState<number | null>(null);
	const [pendingRentSearchAreaMiles, setPendingRentSearchAreaMiles] = useState<number | null>(null);
	const [pendingSoldSearchAreaMiles, setPendingSoldSearchAreaMiles] = useState<number | null>(null);
	
	// Add independent searchAreaPillTouched state for each tab
	const [searchAreaPillTouchedBuy, setSearchAreaPillTouchedBuy] = useState(false);
	const [searchAreaPillTouchedRent, setSearchAreaPillTouchedRent] = useState(false);
	const [searchAreaPillTouchedSold, setSearchAreaPillTouchedSold] = useState(false);

	// Add state for geocoded search location
	const [searchLocationCoords, setSearchLocationCoords] = useState<{ lat: number, lng: number } | null>(null);

	// Add separate state for property class selections
	const [selectedPropertyTypesBuySold, setSelectedPropertyTypesBuySold] = useState([...buyPropertyTypes]);
	const [selectedPropertyTypesRent, setSelectedPropertyTypesRent] = useState([...rentPropertyTypes]);

	// Add separate price range state for rent and buy/sold
	const [priceRangeBuySold, setPriceRangeBuySold] = useState<[number | null, number | null]>([null, null]);
	const [priceRangeRent, setPriceRangeRent] = useState<[number | null, number | null]>([null, null]);

	// Add separate state for beds and baths for rent and buy/sold
	const [bedsBuySold, setBedsBuySold] = useState<number | null>(null);
	const [bedsRent, setBedsRent] = useState<number | null>(null);
	const [bathsBuySold, setBathsBuySold] = useState<number | null>(null);
	const [bathsRent, setBathsRent] = useState<number | null>(null);

	// Add state for city and state search
	const [searchCity, setSearchCity] = useState<string>('');
	const [searchState, setSearchState] = useState<string>('');

	return {
		// HOA state
		maxHoaFees,
		setMaxHoaFees,
		noHoa,
		setNoHoa,
		
		// Search state
		searchQuery,
		setSearchQuery,
		debouncedSearchQuery,
		setDebouncedSearchQuery,
		isSearching,
		setIsSearching,
		listings,
		setListings,
		location,
		setLocation,
		
		// Filter state
		showFilters,
		setShowFilters,
		filters,
		setFilters,
		sort,
		setSort,
		activeFilterTab,
		setActiveFilterTab,
		
		// Modal state
		infoModal,
		setInfoModal,
		showPropertyTypeModal,
		setShowPropertyTypeModal,
		showAmenitiesModal,
		setShowAmenitiesModal,
		showSquareFeetModal,
		setShowSquareFeetModal,
		showLotSizeModal,
		setShowLotSizeModal,
		showHomeAgeDropdown,
		setShowHomeAgeDropdown,
		minHomeAge,
		setMinHomeAge,
		maxHomeAge,
		setMaxHomeAge,
		showDaysOnMarketModal,
		setShowDaysOnMarketModal,
		
		// Property type state
		propertyTypePillTouchedRent,
		setPropertyTypePillTouchedRent,
		propertyTypePillTouchedBuySold,
		setPropertyTypePillTouchedBuySold,
		
		// Animation state
		resetBouncing,
		setResetBouncing,
		daysOnMarketResetBouncing,
		setDaysOnMarketResetBouncing,
		amenitiesResetBouncing,
		setAmenitiesResetBouncing,
		
		// Warning state
		propertyTypeWarning,
		setPropertyTypeWarning,
		
		// Price mode state
		
		// Search area state
		buySearchAreaMiles,
		setBuySearchAreaMiles,
		rentSearchAreaMiles,
		setRentSearchAreaMiles,
		soldSearchAreaMiles,
		setSoldSearchAreaMiles,
		showSearchAreaDropdown,
		setShowSearchAreaDropdown,
		pendingBuySearchAreaMiles,
		setPendingBuySearchAreaMiles,
		pendingRentSearchAreaMiles,
		setPendingRentSearchAreaMiles,
		pendingSoldSearchAreaMiles,
		setPendingSoldSearchAreaMiles,
		searchAreaPillTouchedBuy,
		setSearchAreaPillTouchedBuy,
		searchAreaPillTouchedRent,
		setSearchAreaPillTouchedRent,
		searchAreaPillTouchedSold,
		setSearchAreaPillTouchedSold,
		
		// Location state
		searchLocationCoords,
		setSearchLocationCoords,
		
		// Property type selections
		selectedPropertyTypesBuySold,
		setSelectedPropertyTypesBuySold,
		selectedPropertyTypesRent,
		setSelectedPropertyTypesRent,
		
		// Price range state
		priceRangeBuySold,
		setPriceRangeBuySold,
		priceRangeRent,
		setPriceRangeRent,
		
		// Beds and baths state
		bedsBuySold,
		setBedsBuySold,
		bedsRent,
		setBedsRent,
		bathsBuySold,
		setBathsBuySold,
		bathsRent,
		setBathsRent,
		
		// City and state search
		searchCity,
		setSearchCity,
		searchState,
		setSearchState,
	};
}

export function usePropertyQueries(
	searchQuery: string,
	searchCity: string,
	searchState: string,
	listings: Property[],
	_isSearching: boolean,
	setListings: (listings: Property[]) => void,
	_setIsSearching: (searching: boolean) => void,
	_shouldExecuteSearch: boolean = false,
	activeFilterTab: 'buy' | 'rent' | 'sold' = 'buy',
	onSearchComplete?: () => void, // Callback to reset search state
	selectedPropertyTypes?: string[] // Selected property types from context
) {
	
	// Track search executions to prevent duplicates
	const searchExecutionCount = useRef(0);
	const lastSearchParams = useRef<string>('');
	
	// Track latest listings length to avoid stale closures overriding state
	const latestListingsLenRef = useRef<number>(listings.length);
	useEffect(() => {
		latestListingsLenRef.current = listings.length;
	}, [listings]);

	// Remove automatic cache invalidation - let parent component control this
	// useEffect(() => {
	//   if (searchQuery || searchCity || searchState) {
	//     queryClient.invalidateQueries({ queryKey: ['properties'] });
	//   }
	// }, [searchQuery, searchCity, searchState, queryClient]);

	// Reset search execution tracking when search parameters change
	useEffect(() => {
		// Create simplified search parameters that focus on the actual search query
		const searchQueryKey = searchQuery || `${searchCity}, ${searchState}`;
		const currentParams = JSON.stringify({ searchQuery: searchQueryKey, activeFilterTab });
		
		// Only reset if the actual search location has changed
		if (currentParams !== lastSearchParams.current) {
			lastSearchParams.current = currentParams;
			searchExecutionCount.current = 0;
			
			// Only clear the search key if we don't have matching listings
			// This prevents clearing the key when we return from property view
			if (!listings || listings.length === 0) {

				try {
					localStorage.removeItem('homeFilters_lastSearchKey');

				} catch (error) {

					// localStorage access failed, continue
				}
			} else {

			}
		}
	}, [searchQuery, searchCity, searchState, activeFilterTab]);

	// Save search key when we have search parameters (for immediate cache checking)
	useEffect(() => {
		if (searchQuery || searchCity || searchState) {
			const locationString = searchQuery || `${searchCity}, ${searchState}`;
			const searchKey = `${locationString}_${activeFilterTab}`;
			
			// Only save if we don't already have this search key saved
			try {
				const existingKey = localStorage.getItem('homeFilters_lastSearchKey');
				if (existingKey !== searchKey) {

					
					localStorage.setItem('homeFilters_lastSearchKey', searchKey);

				} else {

				}
			} catch (error) {

			}
		}
	}, [searchQuery, searchCity, searchState, activeFilterTab]);

	// Check if we have cached results for instant back button navigation
	const checkLocalCacheFirst = () => {


		if (!searchQuery && !searchCity && !searchState) {

			return null;
		}
		
		// First check if we have listings in the current state (from localStorage)
		if (listings && Array.isArray(listings) && listings.length > 0) {
			// Verify these listings match the current search parameters
			const locationString = searchQuery || `${searchCity}, ${searchState}`;
			const currentSearchKey = `${locationString}_${activeFilterTab}`;
			

			
			// Check if we have a matching search in localStorage
			try {
				const storedSearchKey = localStorage.getItem('homeFilters_lastSearchKey');

				
				if (storedSearchKey === currentSearchKey) {

					return listings;
				} else {

				}
			} catch (error) {

				// localStorage access failed, continue with other checks
			}
		} else {

		}
		
		// Build the search cache key that matches what was stored during search
		const locationString = searchQuery || `${searchCity}, ${searchState}`;
		const status = activeFilterTab === 'rent' ? 'forRent' : activeFilterTab === 'sold' ? 'sold' : 'forSale';
		const searchParams = {
			location: locationString,
			status,
			page: 0,
			sort: 'relevance'
		};
		
		// Generate cache key using the same format as centralized cache
		const searchType = activeFilterTab === 'rent' ? 'rent' : activeFilterTab === 'sold' ? 'sold' : 'sale';
		const searchCacheKey = generateCacheKey(searchParams, searchType);
		

		
		const cachedResults = propertyCache.get(searchCacheKey);
		
		if (cachedResults && Array.isArray(cachedResults) && cachedResults.length > 0) {

			return cachedResults;
		}
		

		return null;
	};
	
	// Check if we have cached results for back button navigation
	const hasCachedResults = checkLocalCacheFirst();
	
	const shouldEnableQuery = (!!searchQuery || !!searchCity || !!searchState) && !hasCachedResults;

	
	// Always call useQuery to maintain hook consistency, but conditionally enable it
    // Circuit breaker to avoid refetch storms for invalid queries
    const lastFailedByKeyRef = useRef<Map<string, number>>(new Map());

    const { data: queryListings, isLoading, error, refetch, isFetching } =
		useQuery({
			queryKey: ['properties', searchQuery, searchCity, searchState, activeFilterTab],
			enabled: shouldEnableQuery,
			staleTime: 0, // No stale time - always fetch fresh data
			gcTime: 5 * 60 * 1000, // 5 minutes
			refetchOnWindowFocus: false,
			refetchOnMount: false,
			refetchOnReconnect: false,
			queryFn: async () => {
                const searchDisplayQueryKey = (searchQuery || `${searchCity}, ${searchState}` || '').trim().toLowerCase() + '|' + activeFilterTab;
                const lastFailedAt = lastFailedByKeyRef.current.get(searchDisplayQueryKey) || 0;
                if (Date.now() - lastFailedAt < 12000) {
                    return [];
                }
				// Early return if all search fields are empty
				if (!searchQuery && !searchCity && !searchState) {
					return [];
				}

				// Basic check: only proceed if we have search parameters
				if (!searchQuery && !searchCity && !searchState) {
					return [];
				}

				// Create a unique search identifier to prevent duplicates
				const searchParams = JSON.stringify({ searchQuery, searchCity, searchState, activeFilterTab });
				
				// Create a simplified search identifier that focuses on the actual search query
				// const searchQueryKey = searchQuery || `${searchCity}, ${searchState}`;
				
				// Simple duplicate check - only prevent if we have the exact same parameters and results
				if (searchParams === lastSearchParams.current && listings.length > 0) {
					return listings;
				}
				
				// Update tracking
				searchExecutionCount.current += 1;
				lastSearchParams.current = searchParams;
				
				// Dispatch search start event
				const searchDisplayQuery = searchQuery || `${searchCity}, ${searchState}`;
				window.dispatchEvent(new CustomEvent('search:start', {
					detail: { query: searchDisplayQuery, type: activeFilterTab }
				}));
				
				// Add a timeout to the entire query function
				const queryTimeout = new Promise<Property[]>((_, reject) => {
					setTimeout(() => {
						// Dispatch search complete event with timeout
						const searchDisplayQuery = searchQuery || `${searchCity}, ${searchState}`;
						window.dispatchEvent(new CustomEvent('search:complete', {
							detail: { 
								query: searchDisplayQuery, 
								type: activeFilterTab, 
								success: false,
								timeout: true
							}
						}));
						reject(new Error('Query timeout after 30 seconds'));
					}, 30000);
				});

                const queryPromise = (async () => {
					try {
						let query = supabase
							.from('properties')
							.select('*');

						// Simplified search logic - try API first, then cache
						if (searchQuery || searchCity || searchState) {
							try {
								// Build location string
								let location = '';
								if (searchQuery) {
									location = searchQuery;
								} else if (searchCity && searchState) {
									location = `${searchCity}, ${searchState}`;
								} else if (searchCity) {
									location = searchCity;
								} else if (searchState) {
									location = searchState;
								}
								
								// Try API search first
								let apiData: Property[] = [];
								
								// Get selected property types for the current tab
								const currentSelectedPropertyTypes = selectedPropertyTypes || (activeFilterTab === 'rent' ? rentPropertyTypes : buyPropertyTypes);
								
								// Convert property types to API format
								const homeType = convertPropertyTypesForApi(currentSelectedPropertyTypes, activeFilterTab);
								
                                // Guard against late onProgress updates after a timeout
                                let cancelled = false;
                                const cancelTimer = setTimeout(() => { cancelled = true; }, 10500);

                                if (activeFilterTab === 'rent') {
                                    apiData = await searchRentProperties(location, {
										page: 0,
										resultsPerPage: 25,
										sort: 'relevance',
										homeType: homeType
									}, (initialResults, _totalCount) => {
										// Update the listings when search results are available
										if (initialResults && initialResults.length > 0) {
                                            if (!cancelled) {
                                                if (initialResults.length >= listings.length) {
                                                    setListings(initialResults);
                                                }
                                            }
										}
									});
								} else if (activeFilterTab === 'sold') {
									// NEW: Handle sold properties with dedicated sold search service
									apiData = await searchSoldProperties(location, {
										status: 'sold',
										page: 0,
										resultsPerPage: 25,
										sort: 'relevance',
										homeType: homeType
									}, (initialResults: Property[], _totalCount: number) => {
										// Progressive loading: show initial results immediately
										if (initialResults && initialResults.length > 0) {
                                            if (!cancelled) {
                                                if (initialResults.length >= latestListingsLenRef.current) {
                                                    setListings(initialResults);
                                                }
                                            }
										}
									});
								} else {
									// Buy properties (forSale)
									apiData = await searchSaleProperties(location, {
										status: 'forSale',
										page: 0,
										resultsPerPage: 25,
										sort: 'relevance',
										homeType: homeType
									}, (initialResults, _totalCount) => {
										// Progressive loading: show initial results immediately
										if (initialResults && initialResults.length > 0) {
                                            if (!cancelled) {
                                                if (initialResults.length >= latestListingsLenRef.current) {
                                                    setListings(initialResults);
                                                }
                                            }
										}
									});
								}

                                clearTimeout(cancelTimer);
								
                                if (apiData && apiData.length > 0) {
									// Ensure final resolved results update UI (not only progressive batches)
									setListings(apiData);
									
									// Save search key to localStorage for instant back navigation
									const locationString = searchQuery || `${searchCity}, ${searchState}`;
									const searchKey = `${locationString}_${activeFilterTab}`;

									try {
										localStorage.setItem('homeFilters_lastSearchKey', searchKey);

									} catch (error) {

										// localStorage access failed, continue
									}
									
									return apiData;
								}
								
								// Fallback to cache if API returns no results
								let query = supabase.from('properties').select('*');
								
								if (searchQuery) {
									query = query.ilike('address', `%${searchQuery}%`);
								} else if (searchCity && searchState) {
									query = query.eq('city', searchCity).eq('state', searchState);
								} else if (searchCity) {
									query = query.eq('city', searchCity);
								} else if (searchState) {
									query = query.eq('state', searchState);
								}
								
								const { data: cachedData } = await query.limit(20);
								
								if (cachedData && cachedData.length > 0) {
									// Save search key to localStorage for instant back navigation
									const locationString = searchQuery || `${searchCity}, ${searchState}`;
									const searchKey = `${locationString}_${activeFilterTab}`;

									try {
										localStorage.setItem('homeFilters_lastSearchKey', searchKey);

									} catch (error) {

										// localStorage access failed, continue
									}
									return cachedData;
								}
								
                                // Mark failure to avoid immediate refetch storms
                                lastFailedByKeyRef.current.set(searchDisplayQueryKey, Date.now());
                                return [];
							} catch (error) {
								// Final fallback to cache only
								let fallbackQuery = supabase.from('properties').select('*');
								
								if (searchQuery) {
									fallbackQuery = fallbackQuery.ilike('address', `%${searchQuery}%`);
								} else if (searchCity && searchState) {
									fallbackQuery = fallbackQuery.eq('city', searchCity);
								} else if (searchState) {
									fallbackQuery = fallbackQuery.eq('state', searchState);
								}
								
								const { data: fallbackData } = await fallbackQuery.limit(20);
								if (fallbackData && fallbackData.length > 0) {
									// Save search key to localStorage for instant back navigation
									const locationString = searchQuery || `${searchCity}, ${searchState}`;
									const searchKey = `${locationString}_${activeFilterTab}`;

									try {
										localStorage.setItem('homeFilters_lastSearchKey', searchKey);

									} catch (error) {

										// localStorage access failed, continue
									}
								}
                                const result = fallbackData || [];
                                if (!result || result.length === 0) {
                                    lastFailedByKeyRef.current.set(searchDisplayQueryKey, Date.now());
                                }
                                return result;
							}
						} else if (searchCity && searchState) {
							query = query.eq('city', searchCity).eq('state', searchState);
						} else if (searchCity) {
							query = query.eq('city', searchCity);
						} else if (searchState) {
							query = query.eq('state', searchState);
						}

						const { data: cachedData, error: cacheError } = await query.limit(20);

						if (cacheError) {
							throw cacheError;
						}

						if (cachedData && cachedData.length > 0) {
							// Save search key to localStorage for instant back navigation
							const locationString = searchQuery || `${searchCity}, ${searchState}`;
							const searchKey = `${locationString}_${activeFilterTab}`;

							try {
								localStorage.setItem('homeFilters_lastSearchKey', searchKey);

							} catch (error) {

								// localStorage access failed, continue
							}
						}

						return cachedData || [];
					} catch (error) {
						// Dispatch search complete event with failure
						const searchDisplayQuery = searchQuery || `${searchCity}, ${searchState}`;
						window.dispatchEvent(new CustomEvent('search:complete', {
							detail: { 
								query: searchDisplayQuery, 
								type: activeFilterTab, 
								success: false 
							}
						}));
						
						throw error;
					} finally {
						// Dispatch search complete event
						const searchDisplayQuery = searchQuery || `${searchCity}, ${searchState}`;
						window.dispatchEvent(new CustomEvent('search:complete', {
							detail: { 
								query: searchDisplayQuery, 
								type: activeFilterTab, 
								success: true 
							}
						}));
						
						// Call the callback to reset search state in parent component
						if (onSearchComplete) {
							onSearchComplete();
						}
					}
				})();

				return Promise.race([queryPromise, queryTimeout]);
			},
			retry: false, // No retries to prevent duplicate searches
		});

	// Return cached results if available, otherwise return query results
	if (hasCachedResults) {

		return { 
			queryListings: hasCachedResults, 
			isLoading: false, 
			error: null, 
			refetch: () => {}, 
			isFetching: false 
		};
	}
	

	return { queryListings, isLoading, error, refetch, isFetching };
}

export function useFeaturedListings(activeFilterTab: 'buy' | 'rent' | 'sold' = 'buy') {
	return useQuery<Property[]>({
		queryKey: ['featuredProperties', activeFilterTab],
		queryFn: async () => {
			try {
				// Try to get featured listings from cache first
				const { data: cachedData, error: cacheError } = await supabase
					.from('properties')
					.select('*')
					.order('price', { ascending: false })
					.limit(6);

				if (cacheError) throw cacheError;

				// If we have recent cached data, use it
				const recentCache = cachedData?.filter(
					(item) => item.last_sync && new Date(item.last_sync).getTime() > Date.now() - 3600000
				);

				if (recentCache?.length) {
					return recentCache;
				}

				// No hardcoded API searches - only use cached data for featured listings
				// This prevents unnecessary API calls and duplicate data
				return [];
			} catch (error) {
				return [];
			}
		},
		enabled: true,
	});
} 